import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import { Search, ListTodo, CheckCircle2, Clock, AlertTriangle, Bell } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoaderProgress from '../components/ui/loader-progress';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

export default function TasksOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleName = (user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('data_scadenza');
  const [search, setSearch] = useState('');
  const [rifUtenteFilter, setRifUtenteFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [linkedDetailsMap, setLinkedDetailsMap] = useState({});
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [excludeFuture, setExcludeFuture] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get('/lorenzo-users');
      const rawUsers = Array.isArray(res.data) ? res.data : [];
      const sortedUsers = [...rawUsers].sort((a, b) => {
        const aName = [a.name, a.surname].filter(Boolean).join(' ').toLowerCase();
        const bName = [b.name, b.surname].filter(Boolean).join(' ').toLowerCase();
        if (aName && bName) return aName.localeCompare(bName);
        if (aName) return -1;
        if (bName) return 1;
        return 0;
      });
      setUsers(sortedUsers);
    } catch (error) {
      console.error('Error fetching users for tasks overview:', error);
      toast.error('Errore nel caricamento utenti per i task');
    } finally {
      setLoadingUsers(false);
    }
  };

  const hydrateLinkedDetails = async (rows) => {
    const keysToFetch = [];

    rows.forEach((task) => {
      if (!task.rif_source || !task.tipologia) return;
      const key = `${task.tipologia}:${task.rif_source}`;
      if (!linkedDetailsMap[key]) {
        keysToFetch.push({ key, tipologia: task.tipologia, id: task.rif_source });
      }
    });

    if (keysToFetch.length === 0) return;

    try {
      const results = await Promise.all(
        keysToFetch.map(({ key, tipologia, id }) => {
          if (tipologia === 'fascicolo') {
            return api
              .get(`/dossier/${id}`)
              .then((res) => ({ key, type: 'dossier', data: res.data }))
              .catch(() => null);
          }
          if (tipologia === 'pratica') {
            return api
              .get(`/negotiation/${id}`)
              .then((res) => ({ key, type: 'negotiation', data: res.data }))
              .catch(() => null);
          }
          return null;
        })
      );

      setLinkedDetailsMap((prev) => {
        const next = { ...prev };

        results.forEach((item) => {
          if (!item || !item.data) return;
          const data = item.data;

          let subject = '';
          if (data.customer_ragione_sociale) {
            subject = data.customer_ragione_sociale;
          } else if (data.customer_name || data.customer_surname) {
            subject = [data.customer_name, data.customer_surname].filter(Boolean).join(' ');
          } else if (data.debitore_nome || data.debitore_cognome) {
            subject = [data.debitore_nome, data.debitore_cognome].filter(Boolean).join(' ');
          }

          let practice =
            data.category_name ||
            data.tipo_pratica ||
            data.contract_type ||
            data.titolo ||
            data.reference ||
            '';

          let counterpart = data.issuer_name || data.creditore || data.issuer || '';

          next[item.key] = {
            subject,
            practice,
            counterpart
          };
        });

        return next;
      });
    } catch (error) {
      console.error('Error hydrating task linked details:', error);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order,
        dateFilter,
        excludeFuture
      };

      if (search) params.search = search;
      if (rifUtenteFilter) params.rif_utente = rifUtenteFilter;
      if (taskTypeFilter === 'dossier') params.tipologia = 'fascicolo';
      if (taskTypeFilter === 'pratica') params.tipologia = 'pratica';
      if (statusFilter === 'completed') params.eseguito = '1';
      if (statusFilter === 'pending') params.eseguito = '0';
      if (priorityFilter === 'yes') params.prioritario = '1';
      if (priorityFilter === 'no') params.prioritario = '0';

      const res = await api.get('/task', { params });
      const payload = res.data || {};
      const rows = Array.isArray(payload.data) ? payload.data : [];
      setTasks(rows);
      setTotal(payload.total || 0);
      await hydrateLinkedDetails(rows);
    } catch (error) {
      console.error('Error fetching tasks overview:', error);
      toast.error('Errore nel caricamento dei task');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [page, rowsPerPage, order, orderBy, search, rifUtenteFilter, statusFilter, priorityFilter, taskTypeFilter, dateFilter, excludeFuture]);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (rifUtenteFilter) params.rif_utente = rifUtenteFilter;
      if (taskTypeFilter === 'dossier') params.tipologia = 'fascicolo';
      if (taskTypeFilter === 'pratica') params.tipologia = 'pratica';
      if (dateFilter !== 'all') params.dateFilter = dateFilter;
      if (excludeFuture) params.excludeFuture = true;

      const res = await api.get('/task/stats', { params });
      setStats(res.data || null);
    } catch (error) {
      console.error('Error fetching task stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [search, rifUtenteFilter, taskTypeFilter, statusFilter, priorityFilter, dateFilter, excludeFuture]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getTaskDotColor = (task) => {
    if (isTaskCompleted(task)) return 'success.main';
    if (!task.data_scadenza) return null;
    const dueDate = new Date(task.data_scadenza);
    if (Number.isNaN(dueDate.getTime())) return null;
    const now = new Date();
    if (dueDate < now) return 'error.main';
    const diffMs = dueDate.getTime() - now.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays <= 3) return 'warning.main';
    return null;
  };

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('it-IT');
  };

  const stripHtml = (value) => {
    if (!value) return '';
    return String(value).replace(/<[^>]+>/g, '').trim();
  };

  const isTaskCompleted = (task) => {
    const value = task && task.eseguito;
    return value === 1 || value === '1' || value === true || value === 'true';
  };

  const getDeadlineInfo = (task) => {
    if (!task.data_scadenza || isTaskCompleted(task)) return null;
    const dueDate = new Date(task.data_scadenza);
    if (Number.isNaN(dueDate.getTime())) return null;
    const now = new Date();
    const dayMs = 1000 * 60 * 60 * 24;

    if (dueDate < now) {
      const diffDays = Math.max(1, Math.floor((now.getTime() - dueDate.getTime()) / dayMs));
      return {
        text: `Scaduto da ${diffDays} giorni – verrà inoltrato alert all'amministratore`,
        color: 'error.main'
      };
    }

    const diffDays = Math.max(1, Math.ceil((dueDate.getTime() - now.getTime()) / dayMs));
    return {
      text: `In scadenza tra ${diffDays} giorni`,
      color: 'warning.main'
    };
  };

  const formatUser = (task) => {
    const fullName = [task.destinatario_name, task.destinatario_surname].filter(Boolean).join(' ');
    if (fullName) return fullName;
    if (task.rif_utente) return `ID: ${task.rif_utente}`;
    return '-';
  };

  const formatSender = (task) => {
    const fullName = [task.mittente_name, task.mittente_surname].filter(Boolean).join(' ');
    if (fullName) return fullName;
    if (task.rif_mittente) return `ID: ${task.rif_mittente}`;
    return '-';
  };

  const formatType = (task) => {
    if (!task.tipologia) return '-';
    if (task.tipologia === 'fascicolo') return 'Dossier';
    if (task.tipologia === 'pratica') return 'Pratica';
    return task.tipologia;
  };

  const formatLinkedSummary = (task) => {
    if (!task.rif_source || !task.tipologia) return '';
    const key = `${task.tipologia}:${task.rif_source}`;
    const info = linkedDetailsMap[key];
    if (!info) return '';

    const parts = [];
    if (info.subject) parts.push(info.subject);
    if (info.practice) parts.push(info.practice);
    if (info.counterpart) parts.push(`controparte ${info.counterpart}`);

    return parts.join(', ');
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleRifUtenteChange = (value) => {
    setRifUtenteFilter(value || '');
    setPage(0);
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handlePriorityChange = (event) => {
    setPriorityFilter(event.target.value);
    setPage(0);
  };

  const handleTaskTypeChange = (event) => {
    setTaskTypeFilter(event.target.value);
    setPage(0);
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
    setPage(0);
  };

  const handleExcludeFutureChange = (event) => {
    setExcludeFuture(event.target.checked);
    setPage(0);
  };

  const destinationLabel = (task) => {
    if (task.tipologia === 'fascicolo' && task.rif_source) {
      return `Dossier #${task.rif_source}`;
    }
    if (task.tipologia === 'pratica' && task.rif_source) {
      return `Pratica #${task.rif_source}`;
    }
    return '-';
  };

  const handleDestinationClick = (task) => {
    if (task.tipologia === 'fascicolo' && task.rif_source) {
      navigate(`/dossier/${task.rif_source}`);
    } else if (task.tipologia === 'pratica' && task.rif_source) {
      navigate(`/negotiation/${task.rif_source}`);
    }
  };

  const paginatedTasks = tasks;
  const displayName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.name || 'utente collegato';
  const headerTitle = isAdmin ? 'Task di tutti gli utenti' : `Task di ${displayName}`;
  const headerSubtitle = isAdmin
    ? 'Vista centralizzata dei task, filtrabili per utente, stato e priorità.'
    : 'Vista dei tuoi task, filtrabili per stato e priorità.';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ListTodo size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {headerTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {headerSubtitle}
            </Typography>
          </Box>
        </Box>
      </Box>

      {stats && (
        <Box
          sx={{
            mb: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))'
            },
            gap: 2
          }}
        >
          <Card
            onClick={() => {
              setStatusFilter('all');
              setPriorityFilter('all');
              setTaskTypeFilter('all');
            }}
            sx={{
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              border: (statusFilter === 'all' && priorityFilter === 'all' && taskTypeFilter === 'all') ? '2px solid' : 'none',
              borderColor: 'primary.main',
              transition: 'box-shadow 0.2s, transform 0.1s',
              '&:hover': {
                boxShadow:
                  '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ color: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.78)' : t.palette.text.secondary) }}
              >
                Totale task
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stats.total}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.92)' : t.palette.primary.light),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': {
                  color: (t) => (t.palette.mode === 'dark' ? '#0f2742' : t.palette.primary.contrastText),
                  strokeWidth: 2.25
                }
              }}
            >
              <Bell size={18} />
            </Box>
          </Card>

          <Card
            onClick={() => {
              setStatusFilter('pending');
              setPriorityFilter('all');
              setPage(0);
            }}
            sx={{
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              border: statusFilter === 'pending' ? '2px solid' : 'none',
              borderColor: 'warning.main',
              transition: 'box-shadow 0.2s, transform 0.1s',
              '&:hover': {
                boxShadow:
                  '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Da fare
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stats.pending}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Clock size={18} />
            </Box>
          </Card>

          <Card
            onClick={() => {
              setStatusFilter('completed');
              setPriorityFilter('all');
              setPage(0);
            }}
            sx={{
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              border: statusFilter === 'completed' ? '2px solid' : 'none',
              borderColor: 'success.main',
              transition: 'box-shadow 0.2s, transform 0.1s',
              '&:hover': {
                boxShadow:
                  '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Completati
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stats.completed}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckCircle2 size={18} />
            </Box>
          </Card>

          <Card
            onClick={() => {
              setPriorityFilter('yes');
              setStatusFilter('all');
              setPage(0);
            }}
            sx={{
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              border: priorityFilter === 'yes' ? '2px solid' : 'none',
              borderColor: 'error.main',
              transition: 'box-shadow 0.2s, transform 0.1s',
              '&:hover': {
                boxShadow:
                  '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Prioritari
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stats.priority}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AlertTriangle size={18} />
            </Box>
          </Card>
        </Box>
      )}

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            value={search}
            onChange={handleSearchChange}
            placeholder="Cerca per titolo, descrizione o utente..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 260 }}
          />

          {isAdmin && (
            <Autocomplete
              size="small"
              options={users}
              loading={loadingUsers}
              value={users.find((u) => u.id === rifUtenteFilter) || null}
              onChange={(event, newValue) => handleRifUtenteChange(newValue ? newValue.id : '')}
              getOptionLabel={(option) =>
                [option.name, option.surname].filter(Boolean).join(' ') || `ID: ${option.id}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Utente assegnato"
                  InputProps={{
                    ...params.InputProps
                  }}
                  sx={{ minWidth: 220 }}
                />
              )}
            />
          )}

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Stato</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Stato"
              value={statusFilter}
              onChange={handleStatusChange}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="completed">Completati</MenuItem>
              <MenuItem value="pending">Da fare</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="task-type-label">Tipologia</InputLabel>
            <Select
              labelId="task-type-label"
              label="Tipologia"
              value={taskTypeFilter}
              onChange={handleTaskTypeChange}
            >
              <MenuItem value="all">Tutte</MenuItem>
              <MenuItem value="dossier">Dossier</MenuItem>
              <MenuItem value="pratica">Pratica</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={excludeFuture}
                onChange={handleExcludeFutureChange}
                color="primary"
              />
            }
            label="Escludi Task non scaduti"
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="priority-filter-label">Priorità</InputLabel>
            <Select
              labelId="priority-filter-label"
              label="Priorità"
              value={priorityFilter}
              onChange={handlePriorityChange}
            >
              <MenuItem value="all">Tutte</MenuItem>
              <MenuItem value="yes">Prioritari</MenuItem>
              <MenuItem value="no">Non prioritari</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Periodo</InputLabel>
            <Select
              value={dateFilter}
              label="Periodo"
              onChange={handleDateFilterChange}
            >
              <MenuItem value="all">Tutto</MenuItem>
              <MenuItem value="today">Oggi</MenuItem>
              <MenuItem value="yesterday">Ieri</MenuItem>
              <MenuItem value="last_7_days">Ultimi 7 giorni</MenuItem>
              <MenuItem value="last_30_days">Ultimi 30 giorni</MenuItem>
              <MenuItem value="this_month">Mese corrente</MenuItem>
              <MenuItem value="last_month">Mese scorso</MenuItem>
              <MenuItem value="this_year">Anno corrente</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSort('data_scadenza')}
                >
                  Scadenza
                </TableCell>
                <TableCell>Utente</TableCell>
                <TableCell>Mittente</TableCell>
                <TableCell>Tipologia</TableCell>
                <TableCell>Collegato a</TableCell>
                <TableCell>Prioritario</TableCell>
                <TableCell>Eseguito</TableCell>
                <TableCell>Titolo</TableCell>
                <TableCell>Descrizione</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <LoaderProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : paginatedTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    Nessun task trovato.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTasks.map((task) => {
                  const dotColor = getTaskDotColor(task);
                  const deadlineInfo = getDeadlineInfo(task);
                  const linkedSummary = formatLinkedSummary(task);
                  return (
                    <TableRow key={task.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {dotColor && (
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: dotColor
                                }}
                              />
                            )}
                            {task.data_scadenza ? formatDate(task.data_scadenza) : '-'}
                          </Box>
                          {deadlineInfo && (
                            <Typography variant="caption" color={deadlineInfo.color}>
                              {deadlineInfo.text}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatUser(task)}</TableCell>
                      <TableCell>{formatSender(task)}</TableCell>
                      <TableCell>{formatType(task)}</TableCell>
                      <TableCell>
                        {task.rif_source ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                            <Chip
                              label={destinationLabel(task)}
                              size="small"
                              variant="outlined"
                              onClick={() => handleDestinationClick(task)}
                              sx={{ cursor: 'pointer' }}
                            />
                            {linkedSummary && (
                              <Typography variant="caption" color="text.secondary">
                                {linkedSummary}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {task.prioritario ? (
                          <Chip label="Sì" color="error" size="small" />
                        ) : (
                          <Chip label="No" variant="outlined" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {isTaskCompleted(task) ? (
                          <Chip label="Completato" color="success" size="small" />
                        ) : (
                          <Chip label="Da fare" variant="outlined" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{task.titolo || '-'}</TableCell>
                      <TableCell>
                        {task.descrizione ? stripHtml(task.descrizione).slice(0, 120) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
